/**
 * CrewQuickActions Component
 * Individual crew member quick action dropdown
 */

import React, { useRef, useEffect } from 'react';
import { MoreVertical, Clock, CheckCircle, XCircle, Users } from 'lucide-react';

const CrewQuickActions = ({ 
  member, 
  isOpen,
  onToggle,
  onRecordHours,
  onSignOn,
  onSignOff,
  onViewProfile,
  onEditDetails,
  position = 'right' // 'right' or 'left'
}) => {
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onToggle]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onToggle(!isOpen);
        }}
        className="p-1 rounded-md hover:bg-gray-100"
      >
        <MoreVertical className="h-4 w-4 text-gray-500" />
      </button>
      
      {isOpen && (
        <div 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className={`absolute ${position === 'left' ? 'left-0' : 'right-0'} z-50 mt-2 w-48 origin-top-${position} rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
          style={{ 
            position: 'absolute',
            top: '100%',
            [position]: 0,
            zIndex: 9999
          }}>
          <div className="py-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRecordHours(member);
              }}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Clock className="mr-3 h-4 w-4 text-blue-600" />
              Record Hours
            </button>
            {member.status === 'Active' ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSignOff(member);
                }}
                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <XCircle className="mr-3 h-4 w-4 text-gray-600" />
                Sign Off
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSignOn(member);
                }}
                className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <CheckCircle className="mr-3 h-4 w-4 text-green-600" />
                Sign On
              </button>
            )}
            <div className="border-t border-gray-100"></div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile(member);
              }}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Users className="mr-3 h-4 w-4 text-gray-600" />
              View Profile
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditDetails(member);
              }}
              className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Users className="mr-3 h-4 w-4 text-gray-600" />
              Edit Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewQuickActions;